/**
 * PrompterAnimationService
 *
 * Purpose: Isolate 620+ lines of GSAP animation code from NavBarComponent
 * Phase: 1 (Week 1)
 * Risk: LOW - Pure DOM manipulation, no business logic
 * Lines Extracted: 1410-2029 from nav-bar.component.ts
 *
 * Responsibilities:
 * - Manage prompter text animations (moving tags and scramble effects)
 * - Calculate span positions for animation
 * - Handle animation timing and GSAP timeline
 * - Run animations outside Angular zone for performance
 *
 * NOT Responsible For:
 * - Filter state management (stays in FiltersStateService)
 * - HTTP requests (stays in HttpReqService)
 * - Signal updates (stays in components)
 */

import {ElementRef, Injectable, NgZone} from '@angular/core';
import {gsap} from 'gsap';
import {PrompterResult, Tags} from '../../../../interfaces/prompter-result';

export interface SpanPosition {
  id: string;
  top: number;
  left: number;
}

@Injectable()
export class PrompterAnimationService {
  private animationInProgress = false;
  private matchingSpansStartPositions: SpanPosition[] = [];
  private matchingSpansEndPositions: SpanPosition[] = [];
  private nonMatchingSpansEndPositions: SpanPosition[] = [];

  constructor(
    private ngZone: NgZone
  ) {
  }

  /**
   * Main entry point for prompter magic animation
   * Runs outside Angular zone for better performance
   *
   * @param prompterResult - Result from free text query API
   * @param editableDiv - Main search input div
   * @param editableCopyDiv - Hidden copy div for calculations
   * @param skipAnimation - Force scramble animation instead of moving
   * @param onComplete - Callback when animation finishes
   */
  startPrompterMagic(
    prompterResult: PrompterResult,
    editableDiv: ElementRef<HTMLDivElement>,
    editableCopyDiv: ElementRef<HTMLDivElement>,
    skipAnimation?: boolean,
    onComplete?: () => void
  ): void {
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.animationInProgress = true;

        // Determine which animation strategy to use
        if (skipAnimation || prompterResult.skipAnimation || prompterResult.narrative) {
          this.startFakePrompterAnimationFlow(editableDiv, editableCopyDiv, onComplete);
        } else {
          this.startRealPrompterAnimationFlow(prompterResult, editableDiv, editableCopyDiv, onComplete);
        }
      });
    });
  }

  /**
   * Check if animation is currently running
   */
  isAnimating(): boolean {
    return this.animationInProgress;
  }

  /**
   * Force stop any running animations
   */
  stopAnimations(editableDiv: ElementRef<HTMLDivElement>, editableCopyDiv: ElementRef<HTMLDivElement>): void {
    gsap.killTweensOf(editableDiv.nativeElement.querySelectorAll('span'));
    gsap.killTweensOf(editableCopyDiv.nativeElement.querySelectorAll('span'));
    this.animationInProgress = false;
  }

  /**
   * Real prompter animation - tags move to new positions
   * Used when text positions can be calculated (matching original → replaced)
   *
   * Example: "upbeat pop" → "Upbeat Pop" (tags can slide to new positions)
   */
  private startRealPrompterAnimationFlow(
    prompterResult: PrompterResult,
    editableDiv: ElementRef<HTMLDivElement>,
    editableCopyDiv: ElementRef<HTMLDivElement>,
    onComplete?: () => void
  ): void {
    // Convert text to spans with IDs for animation
    this.textToSpansForRealPrompt(editableDiv.nativeElement, prompterResult.tags, true);
    this.textToSpansForRealPrompt(editableCopyDiv.nativeElement, prompterResult.tags, false);

    Promise.resolve().then(() => {
      // Calculate positions of matching spans
      this.matchingSpansStartPositions = this.calculateSpanPositions(
        editableDiv.nativeElement,
        'span[id]'
      );
      this.matchingSpansEndPositions = this.calculateSpanPositions(
        editableCopyDiv.nativeElement,
        'span[id]'
      );

      // If positions don't match, fall back to scramble animation
      if (this.matchingSpansStartPositions.length !== this.matchingSpansEndPositions.length) {
        this.startFakePrompterAnimationFlow(editableDiv, editableCopyDiv, onComplete);
        return;
      }

      // Calculate non-matching text positions (new content that appears)
      this.nonMatchingSpansEndPositions = this.calculateSpanPositions(
        editableCopyDiv.nativeElement,
        '.tag-fade-in'
      );

      this.startPrompterMovingAnimations(editableDiv, editableCopyDiv, onComplete);
    });
  }

  /**
   * Fake prompter animation - scramble effect
   * Used when text positions cannot be calculated or animation skipped
   *
   * Example: Long queries, narrative mode, duplicate words detected
   */
  private startFakePrompterAnimationFlow(
    editableDiv: ElementRef<HTMLDivElement>,
    editableCopyDiv: ElementRef<HTMLDivElement>,
    onComplete?: () => void
  ): void {
    this.textToSpanForFakePrompt(editableDiv.nativeElement);

    Promise.resolve().then(() => {
      this.startPrompterScrambleAnimation(editableDiv, editableCopyDiv, onComplete);
    });
  }

  /**
   * Execute moving animation timeline
   * Tags slide from original positions to new positions
   */
  private startPrompterMovingAnimations(
    editableDiv: ElementRef<HTMLDivElement>,
    editableCopyDiv: ElementRef<HTMLDivElement>,
    onComplete?: () => void
  ): void {
    const tl = gsap.timeline();

    // Step 1: Fade out non-matched spans
    editableDiv.nativeElement.querySelectorAll('.tag-fade-out').forEach((element) => {
      tl.to(
        element,
        {
          duration: 1,
          opacity: 0,
          ease: 'power1.inOut'
        },
        0
      );
    });

    // Step 2: Move matched spans to their new positions
    this.matchingSpansStartPositions.forEach((startPos, index) => {
      const endPos = this.matchingSpansEndPositions.find(
        (pos) => startPos.id.split('original-value-')[1] === pos.id.split('replaced-value-')[1]
      );

      if (endPos) {
        const originalElement = editableDiv.nativeElement.querySelector(`#${startPos.id}`) as HTMLElement;
        const replacementElement = editableCopyDiv.nativeElement.querySelector(`#${endPos.id}`) as HTMLElement;

        const animationDuration = 1.5;
        const classChangeTime = animationDuration - 1.2;

        if (originalElement) {
          originalElement.style.position = 'absolute';
          originalElement.style.zIndex = '15';
          tl.to(
            originalElement,
            {
              duration: 1,
              left: endPos.left,
              top: endPos.top - 2,
              ease: 'power1.inOut',
              onUpdate: () => {
                const currentTime = tl.time();
                if (currentTime >= classChangeTime && !originalElement.classList.contains('updated')) {
                  // Apply class changes based on filter state
                  if (replacementElement.className.includes('text-green-500')) {
                    originalElement.classList.add('text-green-500');
                  }
                  if (replacementElement.className.includes('text-red-600')) {
                    originalElement.classList.add('text-red-600');
                  }
                  originalElement.classList.add('updated');
                  originalElement.textContent = replacementElement.textContent;
                }
              }
            },
            0
          );
        }
      }
    });

    // Step 3: Fade in non-matching spans (new content)
    this.nonMatchingSpansEndPositions.forEach((pos) => {
      const spanContent = pos.id;
      const clone = document.createElement('span');
      clone.textContent = spanContent;
      clone.style.opacity = '0';
      clone.style.position = 'absolute';
      clone.style.left = `${pos.left}px`;
      clone.style.top = `${pos.top}px`;

      editableDiv.nativeElement.appendChild(clone);
      tl.to(clone, {duration: 1, opacity: 1, ease: 'power1.inOut'}, 0.3);
    });

    // Cleanup step
    tl.add(() => {
      editableDiv.nativeElement.querySelectorAll('.updated').forEach((element: Element) => {
        (element as HTMLElement).classList.remove('updated');
      });
    }, '+=0');

    // Move cursor to end
    this.moveCursorToEnd(editableCopyDiv.nativeElement);

    // Finalize timeline
    tl.eventCallback('onComplete', () => {
      // Recombine text in copy div
      editableCopyDiv.nativeElement.innerHTML = this.combineTextInDivNodes(
        editableCopyDiv.nativeElement.childNodes
      );

      // Remove animation classes
      editableCopyDiv.nativeElement.querySelectorAll('.tag-animation').forEach((element: Element) => {
        (element as HTMLElement).classList.remove('tag-animation');
      });

      this.animationInProgress = false;

      if (onComplete) {
        this.ngZone.run(() => onComplete());
      }
    });

    tl.play();
  }

  /**
   * Execute scramble animation timeline
   * Words scatter out and reform with new content
   */
  private startPrompterScrambleAnimation(
    editableDiv: ElementRef<HTMLDivElement>,
    editableCopyDiv: ElementRef<HTMLDivElement>,
    onComplete?: () => void
  ): void {
    // Force reset any ongoing animations
    this.stopAnimations(editableDiv, editableCopyDiv);

    this.ngZone.run(() => {
      this.animationInProgress = true;

      const originalSpans = editableDiv.nativeElement.querySelectorAll('span');

      const timeline = gsap.timeline({
        onComplete: () => {
          this.animationInProgress = false;
        }
      });

      // 1. Color shift and scale down
      timeline.to(originalSpans, {
        color: '#6e6d6d',
        scale: 0.7,
        duration: 0.3,
        stagger: 0.05
      });

      // 2. Scatter and fade out
      timeline.to(originalSpans, {
        x: (i) => gsap.utils.random(-50, 50),
        y: (i) => gsap.utils.random(-30, 30),
        rotation: (i) => gsap.utils.random(-20, 20),
        opacity: 0,
        scale: 0.5,
        duration: 0.3,
        stagger: 0.1,
        ease: 'power1.out'
      });

      // 3. CRITICAL: Swap content
      timeline.add(() => {
        editableDiv.nativeElement.innerHTML = editableCopyDiv.nativeElement.innerHTML;

        const newSpans = editableDiv.nativeElement.querySelectorAll('span');

        // Set initial scattered state
        gsap.set(newSpans, {
          x: (i) => gsap.utils.random(-70, 70),
          y: (i) => gsap.utils.random(-40, 40),
          rotation: (i) => gsap.utils.random(-30, 30),
          scale: 0.3,
          opacity: 0
        });

        // 4. Scatter in with elastic bounce
        gsap.to(newSpans, {
          x: 0,
          y: 0,
          rotation: 0,
          scale: 1,
          opacity: 1,
          color: '',
          duration: 0.5,
          stagger: 0.1,
          ease: 'elastic.out(0.5, 0.3)',
          onComplete: () => {
            editableCopyDiv.nativeElement.innerHTML = '';
            if (onComplete) {
              this.ngZone.run(() => onComplete());
            }
          }
        });
      });
    });
  }

  /**
   * Convert text to spans with IDs for real animation
   * Wraps originalValueFromSearch or replacedWith in spans
   */
  private textToSpansForRealPrompt(
    div: HTMLDivElement,
    tags: Tags[],
    useOriginal: boolean
  ): void {
    let innerText = div.innerText;

    const property = useOriginal ? 'originalValueFromSearch' : 'replacedWith';

    // Wrap each tag value in a span
    tags.forEach((tag) => {
      const escapedValue = this.escapeRegExp(tag[property]);
      const regex = new RegExp(`(${escapedValue})`, 'gi');
      innerText = innerText.replace(regex, '<span class="prompter-tag">$1</span>');
    });

    // Wrap remaining text not matched by tags
    const nonMatchedParts = innerText.split(/(<span class="prompter-tag">.+?<\/span>)/);
    const fadeClass = useOriginal ? 'class="tag-fade-out"' : 'class="tag-fade-in tag-animation"';
    div.innerHTML = nonMatchedParts
      .map((part) => {
        return part.startsWith('<span')
          ? part
          : part
            .split(' ')
            .map((word) => (word.trim() ? `<span ${fadeClass}>${word}</span>` : ''))
            .join(' ');
      })
      .join('');

    // Add attributes to tagged spans
    tags.forEach((tag, index) => {
      div.querySelectorAll('.prompter-tag').forEach((span) => {
        if (span.textContent === tag[property]) {
          const selectedArtwork = tag.selectedArtwork;
          if (selectedArtwork) {
            span.setAttribute('artwork-id', selectedArtwork.id.toString());
            span.classList.add('input-artwork');
          } else if (tag.isArtistSearch || tag.multipleArtworksIds) {
            span.setAttribute('artist-name', tag.replacedWith);
            span.classList.add('input-artist');
          } else {
            span.setAttribute('filter-family-name', tag.filterFamilyName!);
            span.setAttribute('filter-id', tag.filterId!);
            span.setAttribute('filter-state', tag.state ? 'green' : 'red');
          }

          span.setAttribute('id', useOriginal ? `original-value-${index}` : `replaced-value-${index}`);
          span.classList.add('input-filter-container');

          if (!useOriginal) {
            span.classList.add('tag-animation');
            if (!selectedArtwork && !tag.isArtistSearch && !tag.multipleArtworksIds) {
              span.classList.add(tag.state ? 'text-green-500' : 'text-red-600');
            }
          }
          span.setAttribute('contenteditable', 'false');
        }
      });
    });

    if (useOriginal) {
      this.moveCursorToEnd(div);
    }
  }

  /**
   * Convert text to simple spans for fake animation
   * Splits by words and wraps each in span
   */
  private textToSpanForFakePrompt(div: HTMLDivElement): void {
    const words = div.innerText.split(/\s+/);
    div.innerHTML = '';

    words.forEach((word) => {
      const span = document.createElement('span');
      span.textContent = word + ' ';
      div.appendChild(span);
    });
  }

  /**
   * Calculate positions of spans matching a selector
   * Used to determine start/end positions for animation
   */
  private calculateSpanPositions(div: HTMLDivElement, selector: string): SpanPosition[] {
    const spans: NodeListOf<HTMLElement> = div.querySelectorAll(selector);
    return Array.from(spans).map((span: HTMLElement) => {
      return {
        id: selector === '.tag-fade-in' ? span.innerHTML : span.id,
        top: span.offsetTop,
        left: span.offsetLeft
      };
    });
  }

  /**
   * Combine text nodes in div, wrapping non-tag content in mx-1 spans
   */
  private combineTextInDivNodes(children: NodeListOf<ChildNode>): string {
    let combinedText = '';
    let textAccumulator = '';

    children.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        textAccumulator += child.textContent;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as HTMLElement;
        if (element.classList.contains('prompter-tag')) {
          if (textAccumulator) {
            combinedText += `<span class="mx-1">${textAccumulator}</span>`;
            textAccumulator = '';
          }
          combinedText += element.outerHTML;
        } else {
          textAccumulator += element.textContent;
        }
      }
    });

    if (textAccumulator) {
      combinedText += `<span class="mx-1">${textAccumulator}</span>`;
    }

    return combinedText;
  }

  /**
   * Move cursor to end of div
   */
  private moveCursorToEnd(div: HTMLElement): void {
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(div);
    range.collapse(false);
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  /**
   * Escape special regex characters
   */
  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
